import React, { useState, useEffect, useMemo } from "react";
import { useParams, useLocation } from "react-router-dom";
import { API, graphqlOperation } from "aws-amplify";
import { reqsBySalesforceId } from "../../graphql/queries";
import { createRequests, updateRequests } from "../../graphql/mutations";
import { useNavigate } from "react-router-dom";
import { Box } from "theme-ui";
import {
  Heading,
  useNotify,
  Card as LumosCard,
  Button as LumosButton,
} from "@loanpal/lumos__core";
import {
  View,
  Flex,
  Card as ReactCard,
  TextField,
  SelectField,
  TextAreaField,
  Divider,
  Text
} from "@aws-amplify/ui-react";
import { Categories } from "../../utils/categories";
import {
  Initiatives,
  INITIATIVE_OPTIONS,
  getInitiativeById,
  parentOrganizationClientIdFromInitiative,
  integratorIdFromInitiative,
  referenceIdFromInitiative,
  getInitiativeForRequestReference,
} from "../../utils/initiatives";
import logo from "../../images/goodleap-white-orange.svg";
import Loading from "../../utils/loading";
import { getRequests } from "../../graphql/queries";
import Users from "../request/users";
import Offers from "../request/offers";
import {
  phoneDigits,
  moneyStringToNumber
} from "../../utils/format";
import {
  email as validateEmail
} from "../../utils/formValidation";

/** Default new public GoodLink request to Connected Org (no API keys). */
const DEFAULT_PUBLIC_INITIATIVE = Initiatives.QUICKLINKS_CONNECTED;

const initialState = {
  id: "",
  clientId: "",
  status: "WAITINGFORCONNECTION",
  accountName: "",
  salesforce: "",
  salesforceId: "",
  category: "",
  offer: "",
  loanAmount: 0.0,
  referenceId: referenceIdFromInitiative(DEFAULT_PUBLIC_INITIATIVE),
  channel: DEFAULT_PUBLIC_INITIATIVE.channel || "",
  formUrl: "",
  submittingUserFname: "",
  submittingUserLname: "",
  submittingUserEmail: "",
  submittingUserPhone: "",
  submittingUserRole: "",
  submittingUserPassword: "",
  contactEmail: "",
  partnerEmail: "",
  username: "",
  password: "",
  apiKeys: false,
  updateKeys: false,
  notes: "",
  requestNotes: "",
  integratorId: integratorIdFromInitiative(DEFAULT_PUBLIC_INITIATIVE),
  useConnectedOrgs: !!DEFAULT_PUBLIC_INITIATIVE.connectedOrg,
  organizationId: "",
  parentOrganizationClientId:
    parentOrganizationClientIdFromInitiative(DEFAULT_PUBLIC_INITIATIVE),
  initiative: DEFAULT_PUBLIC_INITIATIVE.id,
};

/** `emailRequest=no-email` → NEW; otherwise WAITINGFORCONNECTION (GoodLink connection email). */
function buildFreshFormState(emailRequestParam) {
  return {
    ...initialState,
    status: emailRequestParam === "no-email" ? "NEW" : "WAITINGFORCONNECTION",
  };
}

const DUP_ERROR_SUBSTR = "already exist";

const SRS_ACCOUNT_NAME_SUFFIX = " + SRS";

function accountNameForInitiativeSelection(accountName, initiativeId) {
  const roofHubId = Initiatives.GOODLEAPPLUS_ROOFHUB.id;
  let n = String(accountName ?? "").trimEnd();
  if (n.endsWith(SRS_ACCOUNT_NAME_SUFFIX)) {
    n = n.slice(0, -SRS_ACCOUNT_NAME_SUFFIX.length).trimEnd();
  }
  if (initiativeId === roofHubId && n.length > 0) {
    n = `${n}${SRS_ACCOUNT_NAME_SUFFIX}`;
  }
  return n;
}

/**
 * Whether to skip the Salesforce duplicate (same account + category) check.
 * Uses all matching keys case-insensitively (`AllowDups` and `allowDups` both work).
 * Last value wins when multiple disagree.
 */
function parseAllowDupsSearchParams(params) {
  if (!params) return false;
  const values = [];
  params.forEach((value, key) => {
    if (String(key).toLowerCase() === "allowdups") values.push(value);
  });
  if (values.length === 0) return false;
  const last = String(values[values.length - 1] ?? "").trim().toLowerCase();
  if (last === "0" || last === "false" || last === "no" || last === "off") {
    return false;
  }
  if (
    last === "1" ||
    last === "true" ||
    last === "yes" ||
    last === "on" ||
    last === ""
  ) {
    return true;
  }
  return false;
}

const Home = () => {
// Change environment dev/prod accordingly
const [env] = useState("prod");
const { id } = useParams();
const location = useLocation();
const searchParams = useMemo(
  () => new URLSearchParams(location.search),
  [location.search]
);
const emailRequest = searchParams.get("emailRequest");
const allowDups = useMemo(
  () => parseAllowDupsSearchParams(searchParams),
  [searchParams]
);

const [formState, setFormState] = useState(() => buildFreshFormState(emailRequest));
const [spinner, setSpinner] = useState(false);
const [salesforceErrorFlag, setSalesforceErrorFlag] = useState(false);
const [salesforceError, setSalesforceError] = useState("");
const [requesterError, setRequesterError] = useState(false);
const [partnerError, setPartnerError] = useState(false);
const [disableSendRequestButton, setDisableSendRequestButton] = useState(true);
const [usersItems, setUsersItems] = useState();
const [users, setUsers] = useState();
const [offers, setOffers] = useState();
const [offerItems, setOfferItems] = useState(null);
// const [categories, setCategories] = useState([]);
const [sUser, setUser] = useState();
const { addSnack } = useNotify();
const [initiativeInfoOpen, setInitiativeInfoOpen] = useState(false);

useEffect(() => {
  if (!initiativeInfoOpen) return undefined;
  const onKeyDown = (e) => {
    if (e.key === "Escape") setInitiativeInfoOpen(false);
  };
  window.addEventListener("keydown", onKeyDown);
  return () => {
    window.removeEventListener("keydown", onKeyDown);
  };
}, [initiativeInfoOpen]);
let navigate = useNavigate();

/** allowDups=true clears duplicate messaging only; invalid SFDC URL error is kept. */
useEffect(() => {
  if (!allowDups) return;
  if (!salesforceError?.includes(DUP_ERROR_SUBSTR)) return;
  setSalesforceError("");
  setSalesforceErrorFlag(false);
}, [allowDups, salesforceError]);

const setInput = (key, value) => {
    if (id === "new" && emailRequest === "no-email") {
      if (key === "contactEmail") {
        setRequesterError(false);
      }
      if (key === "partnerEmail") {
        setPartnerError(false);
      }
      if (salesforceError?.includes(DUP_ERROR_SUBSTR)) {
        setSalesforceError("");
        setSalesforceErrorFlag(false);
      }
    }
    setFormState({ ...formState, [key]: value });
  };

  const handleInitiativeChange = (e) => {
    const value = e.target.value;
    if (!value) {
      setFormState((prev) => ({
        ...prev,
        initiative: "",
        channel: "",
        referenceId: "",
        useConnectedOrgs: false,
        parentOrganizationClientId: "",
        integratorId: "",
        accountName: accountNameForInitiativeSelection(prev.accountName, ""),
      }));
      return;
    }
    const ini = getInitiativeById(value);
    if (!ini) {
      setFormState((prev) => ({
        ...prev,
        initiative: value,
        integratorId: "",
        accountName: accountNameForInitiativeSelection(prev.accountName, value),
      }));
      return;
    }
    const nextIntegratorId = integratorIdFromInitiative(ini);
    setFormState((prev) => ({
      ...prev,
      initiative: value,
      channel: ini.channel || "",
      referenceId: referenceIdFromInitiative(ini),
      useConnectedOrgs: !!ini.connectedOrg,
      parentOrganizationClientId: parentOrganizationClientIdFromInitiative(ini),
      integratorId: nextIntegratorId,
      accountName: accountNameForInitiativeSelection(prev.accountName, ini.id),
    }));
  };

useEffect(() => {
  if (id !== "new") {
    fetchRequest(id);
    return;
  }
  setFormState(buildFreshFormState(emailRequest));
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [id, emailRequest]);

useEffect(() => {
  if (id === 'new') {
    if (validateRequestIsReady()) {
      setDisableSendRequestButton(false);
    } else {
      setDisableSendRequestButton(true);
    }
  }
  // eslint-disable-next-line
}, [formState, requesterError, partnerError, salesforceErrorFlag, allowDups]);

useEffect(() => {
  if (formState && formState.apiKeys && formState.clientId) {
    getOffersByCategory(formState.clientId);
    getUsers(formState.clientId);
  }
  // eslint-disable-next-line
}, [formState.apiKeys]);

/** NOTE: No emails and allow dups for new reuqest */
/** Request URL: /request-quicklink/new?emailRequest=no-email&allowDups=true */
/** requesterError: check if the requester email is valid. */
/** partnerError: check if the partner email is valid. */
/** formState.accountName: check if the account name is valid. */
/** formState.salesforceId: check if the Salesforce ID is valid. */
/** formState.category: check if the category is valid. */

const validateRequestIsReady = () => {
  const salesforceOk = allowDups || !salesforceErrorFlag;
  const initiativeOk = id !== "new" || (formState.initiative && formState.initiative !== "");
  if (
    salesforceOk &&
    initiativeOk &&
    !requesterError &&
    !partnerError &&
    formState.accountName !== "" &&
    formState.salesforceId !== "" &&
    formState.category !== ""
  ) {
      return true;
    } else {
      return false
    }
}

const getUsers = async (clientId) => {
    try {
      const myInit0 = {
        queryStringParameters: {
          apiId: `${clientId}ApiId`,
          apiKey: `${clientId}ApiKey`,
          clientId: clientId
        },
      };

      // get users
      await API.get("developerApi", "/users", myInit0)
      .then(async (response) => {
          if (response.data.length > 0) {
            setUsers(response.data);
            let usersArrayOptions = [];
            usersArrayOptions = response.data
              .map((element) => [
                ...usersArrayOptions,
                { name: `${element.email}`, value: element.id },
              ])
              .flat();
            setUsersItems(usersArrayOptions);
          }
      },
      (err) => {
        console.log("Error getting users", err);
      })

    } catch (err) {
      console.log("error getting users", err);
      return false;
    }
  }


const setInputUser = (value) => {
  const user = users.find(element => element.id === value);
  if (user && user.email !== formState.submittingUserEmail) {
    setUser(user);
    setFormState({...formState, submittingUserEmail: user.email, submittingUserFname: user.firstName, submittingUserLname: user.lastName, submittingUserPhone: phoneDigits(user.phoneNumber.number) })
  }
}

const categoryName = (cat) => {
  if (cat === "HOME_IMPROVEMENT") return "HOME IMPROVEMENT";
  else if (cat === "SOLAR") return "SOLAR";
  else if (cat === "BATTERY") return "BATTERY";
  else if (cat === "ROOF") return  "ROOFING / WINDOWS / DOORS / SIDING / GEOTHERMAL / RESTORATION";
  else if (cat === "REMODELING") return "REMODELING";
}

// const categoryCode = (cat) => {
//   if (cat === "HOME IMPROVEMENT") return "HOME_IMPROVEMENT";
//   else if (cat === "SOLAR") return "SOLAR";
//   else if (cat === "BATTERY") return "BATTERY";
//   else if (cat === "ROOFING / WINDOWS / DOORS / SIDING / GEOTHERMAL / RESTORATION") return  "ROOF";
//   else if (cat === "REMODELING") return "REMODELING";
// }

const getOffersByCategory = async (clientId) => {
    try {
      setSpinner(true);
      const myInit0 = {
        queryStringParameters: {
          apiId: `${clientId}ApiId`,
          apiKey: `${clientId}ApiKey`,
          clientId: clientId
        },
      };

      // get categoryId
      await API.get("developerApi", "/categories", myInit0)
      .then(async (response) => {
          if (response.length > 0) {
              // setCategories(response);
              const cat = response.find(el => el.name === categoryName(formState.category));
            const myInit1 = {
              queryStringParameters: {
                apiId: `${clientId}ApiId`,
                apiKey: `${clientId}ApiKey`,
                clientId: clientId,
                categoryId: cat.id,
                env: env
              },
            };

            // get offersByCategory
            await API.get("developerApi", "/offersByCategory", myInit1)
            .then((response) => {
              if (response.data.length > 0) {

               setOffers(response.data);
               let offersArrayOptions = [];
               offersArrayOptions = response.data
                  .map((element) => [
                    ...offersArrayOptions,
                    { name: element.productCode.includes("DEFERRED") ? `${element.productName} - ${element.name}` : `${element.name} - ${element.productName}`, value: element.id },
                  ])
                  .flat();
               setOfferItems(offersArrayOptions);
              }
            })
          }
        setSpinner(false);
      },
      (err) => {
        addSnack({ variant: 'danger', message: `Error getting offers!` });
        console.log("Error getting offers", err);
        setSpinner(false);
      })

    } catch (err) {
      addSnack({ variant: 'danger', message: `Error getting offers, check that Category is correct!` });
      console.log("Error getting offers", err);
      setSpinner(false);
      return false;
    }
  };

async function fetchRequest(id) {
  if (!id && id === "") return;
  try {
    const reqData = await API.graphql(
      graphqlOperation(getRequests, { id: id })
    );
    const req = reqData.data.getRequests;
    if (req) {
      loadRequest(req);
    }
  } catch (err) {
    console.log("error fetching Request", err);
  }
}

  const loadRequest = (req) => {
    const reqState = {
      id: req.id,
      clientId: req.clientId,
      status: req.status,
      accountName: req.accountName,
      salesforce: req.salesforce,
      salesforceId: req.salesforceId,
      offer: req.offer,
      loanAmount: req.loanAmount,
      channel: req.channel,
      category: req.category,
      sendEmail: req.sendEmail,
      referenceId: req.referenceId,
      submittingUserFname: req.submittingUser.firstName,
      submittingUserLname: req.submittingUser.lastName,
      submittingUserEmail: req.submittingUser.email,
      submittingUserPhone: req.submittingUser.phone,
      submittingUserPassword: "",
      contactEmail: req.contactEmail,
      partnerEmail: req.partnerEmail ? req.partnerEmail : "",
      username: "",
      password: "",
      updateKeys: false,
      apiKeys: req.apiKeys,
      notes: req.notes,
      requestNotes: req.requestNotes,
      singleOfferLinks: req.singleOfferLinks,
      useConnectedOrgs: req.useConnectedOrgs,
      organizationId: req.organizationId ? req.organizationId : "",
      integratorId: req.integratorId ? req.integratorId : "",
      parentOrganizationClientId: req.parentOrganizationClientId ? req.parentOrganizationClientId : "",
      initiative:
        getInitiativeForRequestReference(req.referenceId)?.id ||
        req.referenceId ||
        "",
    };
    console.log("loadRequest reqState", reqState);
    setFormState(reqState);
  };

const getStringBetween = (str, start, end) => {
  try {
    const result = str.match(new RegExp(start + "(.*)" + end));
    return result[1];
  } catch(e) {
    console.log("ERROR");
    return "";
  }
}

const checkRequestByCateogry = (reqs, category) =>{
  const exists = reqs.find(r => r.category === category);
  return exists;
}

const checkRequestExists = async (salesforceUrl, category) => {
    if (allowDups) {
      return false;
    }
    const parsedFromUrl =
      salesforceUrl && salesforceUrl !== ""
        ? getStringBetween(salesforceUrl, "Account/", "/view")
        : "";
    const salesforceId =
      parsedFromUrl && parsedFromUrl !== ""
        ? parsedFromUrl
        : formState.salesforceId;
    if (!salesforceId || !category) {
      return false;
    }
    const reqData = await API.graphql(
      graphqlOperation(reqsBySalesforceId, { salesforceId })
    );
    if (reqData.data.reqsBySalesforceId.items.length > 0) {
      const reqs = reqData.data.reqsBySalesforceId.items;
      console.log(reqs);
      const found = checkRequestByCateogry(reqs, category);
      console.log(found);
      if (found !== undefined) {
        setSalesforceErrorFlag(true);
        setSalesforceError("Request already exist for this account");
        return true;
      }
      setSalesforceErrorFlag(false);
      setSalesforceError("");
      return false;
    }
    setSalesforceErrorFlag(false);
    setSalesforceError("");
    return false;
  };

  const validateSimpleRequest = async (formState) => {
    let error = "";
    if (!formState.contactEmail) error += " [Requester Email]";
    if (!formState.accountName) error += " [Account SFDC Name]";
    if (!formState.salesforce) error += " [SDFC Link]";
    if (!formState.category) error += " [Category]";
    if (!formState.clientId) error += " [Client ID]";
    if (!formState.initiative) error += " [Initiative]";
    if (error !== "") {
      addSnack({ variant: 'danger', message: `Please enter ${error}` });
      setSpinner(false);
      return false;
    }
    if (allowDups && salesforceError?.includes(DUP_ERROR_SUBSTR)) {
      setSalesforceError("");
      setSalesforceErrorFlag(false);
    }
    if (formState.salesforce && formState.category && !allowDups) {
      const exists = await checkRequestExists(formState.salesforce, formState.category);
      console.log("exists", exists, !!exists);
      if (!!exists) {
          addSnack({ variant: 'danger', message: `Request already exist for this account and category!` });
          setSpinner(false);
          return false;
      }
    }
    return true;
  }

  const validateRequest = async (formState) => {
    let error = "";
      console.log(formState);
      if (!formState.accountName) error += " [Account Name]";
      if (!formState.contactEmail) error += " [Requester Email]";
      if (!formState.partnerEmail) error += " [Partner Primary Contact Email]";
      if (!formState.salesforce) error += " [SDFC Link]";
      if (!formState.category) error += " [Category]";
      if (!formState.clientId) error += " [Client ID]";
      if (!formState.offer) error += " [Offers and Save them]";
      if (formState.loanAmount === 0) error += " [Loan Amount]";
      if (!formState.submittingUserEmail) error += " [Submitting User Email]";
      if (!formState.submittingUserFname) error += " [Submitting User First Name]";
      if (!formState.submittingUserLname) error += " [Submitting User Last Name]";
      if (!formState.submittingUserPhone) error += " [Submitting User Phone]";
      if (error !== "") {
        addSnack({ variant: 'danger', message: `Please enter ${error}` });
        setSpinner(false);
        return false;
      }
      return true;
  }

  const addASnack = (status, message) => {
    if (status === "success") {
      addSnack({ variant: 'success', message: `${message} saved!` });
    } else {
      addSnack({ variant: 'danger', message: `Error saving ${message}!` });
    }
  }

  async function addRequest() {
    try {
        setSpinner(true);
        let createIt = false;
        if (id !== "new") {
          createIt = await validateRequest(formState);
        } else {
          createIt = await validateSimpleRequest(formState);
        }
          if (createIt) {
            const app = buildApp(formState, formState.id !== "");
            if (formState.id !== "") {
              await API.graphql(graphqlOperation(updateRequests, { input: app }));
              sendEmailToAdmin();
              addSnack({ variant: 'success', message: 'Request saved successfully!' });
            } else {
              await API.graphql(
                graphqlOperation(createRequests, { input: app })
              );
              addSnack({ variant: 'success', message: 'Request created successfully!' });
            }
            clearForm();
            const nextQs = new URLSearchParams();
            if (allowDups) nextQs.set("allowDups", "true");
            if (emailRequest) nextQs.set("emailRequest", emailRequest);
            const q = nextQs.toString();
            navigate(
              q ? `../request-quicklink/new?${q}` : `../request-quicklink/new`,
              { replace: false }
            );
          }
    } catch (err) {
      console.log("Error saving request:", err);
      addSnack({ variant: 'danger', message: 'Error saving Request' });
    }
    setSpinner(false);
  }

  const sendEmailToAdmin = async () => {
  try {
    if (formState.apiKeys && formState.id !== "" && formState.contactEmail !== "" && formState.category !== "" && formState.accountName !== "") {
      const myParams = {
        queryStringParameters: {
          accountName: formState.accountName,
          id: formState.id,
          category: formState.category,
          contactEmail: formState.contactEmail,
          reason: formState.status === "READYFORREQUESTER" ? "Request is ready for GoodLink:" : "Request to update GoodLink:",
          env: env
        },
      };
      console.log("send email", myParams);
      await API.post("developerApi", "/requestReadyForQuickLinkEmail", myParams);
    }
  } catch (err) {
    console.log("Error Sending Email:", err);
  }
}

  const buildApp = (app, update) => {
    if (update)
      return {
        id: app.id !== "" ? app.id : "",
        clientId: app.clientId,
        status: app.status === "READYFORREQUESTER" || app.status === "NEWPLUS" ? "NEWPLUS" : "UPDATEQUICKLINK",
        accountName: app.accountName,
        salesforce: app.salesforce,
        salesforceId: app.salesforceId,
        offer: app.offer,
        channel: app.channel,
        category: app.category,
        loanAmount: app.loanAmount,
        submittingUser: {
          firstName: app.submittingUserFname,
          lastName: app.submittingUserLname,
          email: app.submittingUserEmail,
          phone: app.submittingUserPhone,
        },
        sendEmail: app.sendEmail,
        referenceId: app.referenceId,
        contactEmail: app.contactEmail,
        partnerEmail: app.partnerEmail,
        apiKeys: app.apiKeys,
        notes: app.notes,
        requestNotes: app.requestNotes,
        useConnectedOrgs: app.useConnectedOrgs ?? false,
        organizationId: app.organizationId ?? "",
        parentOrganizationClientId: app.parentOrganizationClientId ?? "",
        integratorId: app.integratorId ?? "",
      };
    else
      return {
        clientId: app.clientId,
        status: app.status,
        accountName: app.accountName,
        salesforce: app.salesforce,
        salesforceId: app.salesforceId,
        offer: app.offer,
        offersRequested: app.offersRequested,
        channel: app.channel,
        category: app.category,
        loanAmount: app.loanAmount,
        submittingUser: {
          firstName: app.submittingUserFname,
          lastName: app.submittingUserLname,
          email: app.submittingUserEmail,
          phone: app.submittingUserPhone,
        },
        sendEmail: app.sendEmail,
        referenceId: app.referenceId,
        contactEmail: app.contactEmail,
        partnerEmail: app.partnerEmail,
        apiKeys: app.apiKeys,
        notes: app.notes,
        requestNotes: app.requestNotes,
        useConnectedOrgs: app.useConnectedOrgs ?? false,
        organizationId: app.organizationId ?? "",
        parentOrganizationClientId: app.parentOrganizationClientId ?? "",
        integratorId: app.integratorId ?? "",
      };
  };

const clearForm = () => {
    setFormState(buildFreshFormState(emailRequest));
  };

 return (
    <>
    {spinner && <Loading />}
    <Box
          sx={{
            maxWidth: "100%",
            backgroundColor: "#ffffff",
            mx: "auto",
            minHeight: '100vh'
          }}
        >
          <Box>
            <Heading
              className="heading-box"
              sx={{
                width: "960px",
                margin: "0 auto",
                backgroundColor: "#003439",
              }}
            >
              <img src={logo} alt="GoodLeap" style={styles.logo} />
            </Heading>
          </Box>
          <div style={styles.container}>
            {id === 'new' && (
              <>
              <ReactCard style={styles.reminderCard}>
                <Heading ml={1} variant="h3">
                  <View>Important Reminders</View>
                </Heading>
                <Divider
                  orientation="horizontal"
                  style={styles.reminderDivider}
                  size="small"
                />
                <ul style={styles.reminderList}>
                  <li>Make sure you have created the GoodLink channel in their Origin instance</li>
                  <li>Make sure the person to contact has an account created in Origin</li>
                  <li>If these 2 things are not done ahead of time this could add delays to the turnaround time</li>
                </ul>
              </ReactCard>
              </>
            )}
            <Heading mb={3} ml={1} variant="h3">
              <View>GoodLink Request</View>
              {formState && formState.id !== "" && formState.status !== "READYFORREQUESTER" && formState.status !== "NEWPLUS"  && (
                <Text style={styles.subTitle}>To request changes please use the Notes field at the bottom of the form</Text>
              )}
            </Heading>
            <ReactCard>
              <TextField
                label={<Text>
                    Your Email - Who is requesting this GoodLink?
                    <Text as="span" fontSize="0.8rem" color="#660000">
                        {' '}
                    (required)
                    </Text>
                    </Text>}
                onChange={(event) =>
                  setInput("contactEmail", event.target.value)
                }
                onBlurCapture={({target}) => {
                  if (!validateEmail(target.value)) {
                    setRequesterError(true);
                  } else {
                    setRequesterError(false);
                  }
                }}
                size="small"
                width="75%"
                style={styles.input}
                value={formState.contactEmail}
                hasError={requesterError}
                errorMessage="Please enter a valid email address"
                isDisabled={formState.status !== "NEW" && formState.status !== "READYFORREQUESTER" && formState.status !== "NEWPLUS" && formState.status !== "WAITINGONAPIKEYS" && formState.status !== "WAITINGFORCONNECTION"}
              />
            </ReactCard>
            <ReactCard>
              <TextField
                label={<Text>
                    Partner Primary Contact Email - Who is the contact on the partner's side?
                    <Text as="span" fontSize="0.8rem" color="#660000">
                        {' '}
                    (required)
                    </Text>
                    </Text>}
                onChange={(event) =>
                  setInput("partnerEmail", event.target.value)
                }
                onBlurCapture={({target}) => {
                  if (!validateEmail(target.value)) {
                    setPartnerError(true);
                  } else {
                    setPartnerError(false);
                  }
                }}
                size="small"
                width="75%"
                style={styles.input}
                value={formState.partnerEmail}
                hasError={partnerError}
                errorMessage="Please enter a valid email address"
                descriptiveText={"Partner Primary Contact in Salesforce Account Details"}
                isDisabled={formState.status !== "NEW" && formState.status !== "READYFORREQUESTER" && formState.status !== "NEWPLUS" && formState.status !== "WAITINGONAPIKEYS" && formState.status !== "WAITINGFORCONNECTION"}
              />
            </ReactCard>
            <ReactCard>
              <TextField
                onChange={(event) => setInput("accountName", event.target.value)}
                size="small"
                width="75%"
                value={formState.accountName}
                label={<Text>
                    Requesting Partner Name
                    <Text as="span" fontSize="0.8rem" color="#660000">
                        {' '}
                    (required)
                    </Text>
                    </Text>}
                style={styles.input}
                descriptiveText={"Please copy/paste exact name as in SDFC Account Name Field"}
                isDisabled={formState.status !== "NEW" && formState.status !== "READYFORREQUESTER" && formState.status !== "NEWPLUS" && formState.status !== "WAITINGONAPIKEYS" && formState.status !== "WAITINGFORCONNECTION"}
              />
            </ReactCard>
            <ReactCard>
              <Box style={styles.urlContainer}>
                <TextField
                  onChange={(event) => setInput("salesforce", event.target.value)}
                  size="small"
                  width="75%"
                  value={formState.salesforce}
                  label={<Text>
                      Requesting Partner SFDC link
                      <Text as="span" fontSize="0.8rem" color="#660000">
                          {' '}
                      (required)
                      </Text>
                      </Text>}
                  descriptiveText="e.g. https://goodleap.lightning.force.com/lightning/r/Account/0015G000023BS4VQAW/view"
                  style={styles.input}
                  onBlurCapture={({target}) => {
                    if (target.value !== "") {
                      const salesforceId = getStringBetween(target.value, "Account/", "/view" );
                      console.log("salesforceId", salesforceId);
                      if (salesforceId !== "") {
                        setFormState({ ...formState, salesforceId: salesforceId });
                        setSalesforceErrorFlag(false);
                        setSalesforceError("");
                      } else {
                        setSalesforceErrorFlag(true);
                        setSalesforceError("This is not a valid SFDC link");
                      }
                    }
                  }}
                  hasError = {salesforceErrorFlag}
                  errorMessage={salesforceError}
                  isDisabled={formState.status !== "NEW" && formState.status !== "READYFORREQUESTER" && formState.status !== "NEWPLUS" && formState.status !== "WAITINGONAPIKEYS" && formState.status !== "WAITINGFORCONNECTION"}
                />
              </Box>
            </ReactCard>
            <ReactCard>
              <TextField
                onChange={(event) => setInput("clientId", event.target.value)}
                size="small"
                width="75%"
                value={formState.clientId}
                label={<Text>
                    Requesting Partner Client ID
                    <Text as="span" fontSize="0.8rem" color="#660000">
                        {' '}
                    (required)
                    </Text>
                    </Text>}
                style={styles.input}
                descriptiveText={"This is the client ID in Salesforce, you can also find it in Bridge"}
                isDisabled={formState.status !== "NEW" && formState.status !== "READYFORREQUESTER" && formState.status !== "NEWPLUS" && formState.status !== "WAITINGONAPIKEYS" && formState.status !== "WAITINGFORCONNECTION"}
              />
            </ReactCard>
            <ReactCard>
              <SelectField
                style={styles.selectField}
                label={
                  <Flex
                    direction="row"
                    alignItems="center"
                    gap="0.5rem"
                    flexWrap="wrap"                    
                  >
                    <Text style={styles.selectLabel}>
                      What Initiative is this GoodLink for?
                      <Text as="span" fontSize="0.8rem" color="#660000">
                        {" "}
                        (required)
                      </Text>
                    </Text>
                    <button
                      type="button"
                      style={styles.initiativeInfoButton}
                      onClick={() => setInitiativeInfoOpen(true)}
                      aria-label="About initiatives"
                      title="About initiatives"
                    >
                      i
                    </button>
                  </Flex>
                }
                value={formState.initiative}
                onChange={handleInitiativeChange}
                size="small"
                width="75%"
                isDisabled={formState.status !== "NEW" && formState.status !== "READYFORREQUESTER" && formState.status !== "NEWPLUS" && formState.status !== "WAITINGONAPIKEYS" && formState.status !== "WAITINGFORCONNECTION"}
              >
                <option value="">Please select an Initiative</option>
                {INITIATIVE_OPTIONS.map((ini) => (
                  <option key={ini.id} value={ini.id}>
                    {ini.name}
                  </option>
                ))}
              </SelectField>
            </ReactCard>
            <Divider orientation="horizontal" style={styles.divider} size="small" />
            <ReactCard>
              <SelectField
                style={styles.selectField}
                label={<Text style={styles.selectLabel}>
                    What Category is this GoodLink for?
                    <Text as="span" fontSize="0.8rem" color="#660000">
                        {' '}
                    (required)
                    </Text>
                    </Text>}
                value={formState.category}
                onChange={(e) => setInput("category", e.target.value)}
                size="small"
                width="75%"
                isDisabled={formState.status !== "NEW" && formState.status !== "READYFORREQUESTER" && formState.status !== "NEWPLUS" && formState.status !== "WAITINGONAPIKEYS" && formState.status !== "WAITINGFORCONNECTION"}
              >
                <option value="">Please select a Category</option>
                <option value={Categories.HOME_IMPROVEMENT.cat}>{Categories.HOME_IMPROVEMENT.cat}</option>
                <option value={Categories.ROOF.cat}>{Categories.ROOF.cat}</option>
                <option value={Categories.SOLAR.cat}>{Categories.SOLAR.cat}</option>
                <option value={Categories.BATTERY.cat}>{Categories.BATTERY.cat}</option>
                <option value={Categories.REMODELING.cat}>{Categories.REMODELING.cat}</option>
              </SelectField>
            </ReactCard>
            <Divider orientation="horizontal" style={styles.divider} size="small" />
            <ReactCard>
              <TextField
                onChange={(event) => setInput("loanAmount", event.target.value)}
                onBlurCapture={({target}) => {
                  setInput("loanAmount", moneyStringToNumber(target.value));
                }}
                size="small"
                width="75%"
                style={styles.input}
                descriptiveText="Two point precision decimal."
                value={formState.loanAmount}
                label={<Text>
                    What average loan amount should be display by default?
                    <Text as="span" fontSize="0.8rem" color="#660000">
                        {' '}
                    (required)
                    </Text>
                    </Text>}
                isDisabled={formState.status !== "NEW" && formState.status !== "READYFORREQUESTER" && formState.status !== "NEWPLUS" && formState.status !== "WAITINGONAPIKEYS" && formState.status !== "WAITINGFORCONNECTION"}
              />
            </ReactCard>
            <ReactCard>
              <Text style={styles.sectionTitle}>Which authorized Origin user should be notified and assigned to the application when it is submitted?</Text>
              <Flex direction="row" alignItems="flex-start" gap="1rem" style={styles.userFields}>
                <TextField
                  label={<Text>
                    Email
                    <Text as="span" fontSize="0.8rem" color="#660000">
                        {' '}
                    (required)
                    </Text>
                    </Text>}
                  onChange={(event) => setInput("submittingUserEmail", event.target.value.toLowerCase())}
                  size="small"
                  width="75%"
                  value={formState.submittingUserEmail}
                  style={styles.input}
                  isDisabled={formState.status !== "NEW" && formState.status !== "READYFORREQUESTER" && formState.status !== "NEWPLUS" && formState.status !== "WAITINGONAPIKEYS" && formState.status !== "WAITINGFORCONNECTION"}
                />
              </Flex>
            </ReactCard>
            {id !== 'new' && (
              <>
                <ReactCard>
                  <Offers offersArray={formState.offer} offerItems={offerItems} active={offers} setInput={setInput} addSnack={addASnack} readOnly={!!formState.status && formState.status !== "READYFORREQUESTER" && formState.status !== "NEWPLUS" } />
                </ReactCard>
                <LumosCard
                  header={{
                    title: "Submitting User",
                  }}
                  sx={{ marginTop: 20, marginBottom: 20, width: "100%" }}
                >
                  <ReactCard>
                    <Text style={styles.Title2}>Select from the list of existing users below or add a new one.</Text>
                  </ReactCard>
                  <ReactCard>
                    <Users usersItems={usersItems} setInput={setInputUser} sUser={sUser} users={users} readOnly={!!formState.status && formState.status !== "READYFORREQUESTER" && formState.status !== "NEWPLUS" } />
                  </ReactCard>
                  <ReactCard>
                    <TextAreaField
                      style={styles.input}
                      label="Notes"
                      size="default"
                      onChange={(event) => setInput("requestNotes", event.target.value)}
                      rows="3"
                      direction="column"
                      value={formState.requestNotes}
                      descriptiveText={"Use this section to add special instructions or requests"}
                    />
                  </ReactCard>
                </LumosCard>
              </>
            )}
            <Divider
              orientation="horizontal"
              style={styles.divider}
              size="small"
            />
            {id === 'new' && (
              <LumosButton
                sx={{ margin: "20px 10px 40px 10px", padding: "10px", width: "130px", height: "40px" }}
                onClick={addRequest}
                disabled={disableSendRequestButton}
              >
                Send request
              </LumosButton>
            )}
            {id !== 'new' && (
              <LumosButton
                sx={{ margin: "20px 10px 40px 10px", padding: "10px", height: "40px" }}
                onClick={addRequest}
              >
                Send request
              </LumosButton>
            )}
          </div>
    </Box>
    {initiativeInfoOpen && (
      <Box
        role="presentation"
        onClick={() => setInitiativeInfoOpen(false)}
        sx={{
          position: "fixed",
          inset: 0,
          zIndex: 10050,
          backgroundColor: "rgba(0, 0, 0, 0.45)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 3,
        }}
      >
        <Box
          role="dialog"
          aria-modal="true"
          aria-labelledby="initiative-info-title"
          onClick={(e) => e.stopPropagation()}
          sx={{
            backgroundColor: "#ffffff",
            borderRadius: "6px",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.18)",
            maxWidth: "min(36rem, calc(100vw - 2rem))",
            width: "100%",
            padding: 3,
            overflow: "visible",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Flex
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ gap: 2, marginBottom: 2, flexShrink: 0 }}
          >
            <Heading
              id="initiative-info-title"
              as="h2"
              sx={{ fontSize: "1.1rem", margin: 0, color: "#003439" }}
            >
              About initiatives
            </Heading>
            <LumosButton
              type="button"
              variant="ghost"
              onClick={() => setInitiativeInfoOpen(false)}
              sx={{ flexShrink: 0 }}
            >
              Close
            </LumosButton>
          </Flex>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: ["1fr", "1fr 1fr"],
              gap: [2, 3],
              columnGap: 3,
              overflow: "visible",
            }}
          >
            {INITIATIVE_OPTIONS.map((ini) => (
              <Box key={ini.id}>
                <Text as="p" fontWeight="bold" marginBottom="0.15rem" fontSize="0.8rem">
                  {ini.name}
                </Text>
                <Text
                  as="p"
                  color="#374151"
                  fontSize="0.75rem"
                  lineHeight={1.4}
                >
                  {ini.description}
                </Text>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    )}
    </>
 );
};

 const styles = {
  requiredInput: {
    borderColor: "#000000",
    padding: 8,
    fontSize: 16
  },
  selectField: {
    padding: 8,
    fontSize: 14
  },
  selectLabel: {
    fontSize: 14,
    fontWeight: "400"
  },
  input: {
    // marginBottom: 20,
    padding: 8,
    fontSize: 16
  },
  divider: { margin: "5px 0", color: "white", borderColor: "white" },
  container: {
    width: 960,
    margin: "0 auto",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    padding: "20px 20px"
  },
  subTitle: {
    fontSize: "14px",
    fontWeight: "200"
  },
  Title2: {
    fontSize: "16px",
    fontWeight: "300"
  },
  logo: {
    width: 250,
    margin: "0 auto",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    padding: 10,
  },
  heading: {
    backgroundColor: "#003439",
  },
  column: {
    display: "flex",
    flexDirection: "column",
    gap: "1rem"
  },
  reminderCard: {
    marginBottom: "2rem",
    backgroundColor: "#F9FAFB"
  },
  reminderTitle: {
    fontSize: "18px",
    fontWeight: "bold",
    marginBottom: "1rem"
  },
  reminderDivider: {
    margin: "0.5rem 0",
    borderColor: "#f1f1f1"
  },
  reminderList: {
    listStyleType: "disc",
    paddingLeft: "1.5rem",
    margin: 0
  },
  urlContainer: {
    width: "100%",
    maxWidth: "100%",
    overflow: "hidden",
    "& input": {
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis"
    }
  },
  sectionTitle: {
    fontSize: "14px",
    fontWeight: "400",
    position: "relative",
    top: "0.5rem"
  },
  userFields: {
    marginTop: "1rem"
  },
  initiativeInfoButton: {
    width: 22,
    height: 22,
    borderRadius: "50%",
    border: "1px solid #003439",
    backgroundColor: "#ffffff",
    color: "#003439",
    fontSize: 12,
    fontWeight: 700,
    fontStyle: "italic",
    lineHeight: 1,
    cursor: "pointer",
    padding: 0,
    flexShrink: 0,
  },
};

export default Home;